import {
  useBoolean,
  useNumber,
  useString,
  useWindowSize,
  useSearchParams,
} from "helpers/hooks"
import PageLayout from "pages/layout/PageLayout"
import SimpleTable, { SimpleTableProps } from "components/Table/SimpleTable"
import { UserQueryState, UserResponse } from "./user.type"
import { useCallback, useEffect, useState } from "react"
import {
  getAllRoleMiddleware,
  getAllUserMiddleware,
  removeUserInWorkspaceMiddleware,
  updateUserBlockMiddleware,
  updateUserRoleMiddleware,
} from "./api.service"
import { Avatar } from "components/Avatar"
import { Pagination } from "components/Pagination/Pagination"
import { TippyCustomzie, useTippyLayout } from "components/TippyCustomzie"
import ActionItem from "pages/projects/molecules/ActionItem"
import { ReactComponent as IconActionThreeDot } from "assets/images/icons/icon-three-dots.svg"
import { ReactComponent as IconSearchNormal } from "assets/images/icons/icon-search-normal.svg"
import InputDefault from "components/Input/InputDefault"
import { cloneDeep, debounce, includes } from "lodash"
import { ReactComponent as IconClearSearch } from "assets/images/icons/icon-clear-search.svg"
import { ClearAll, FilterIcon } from "components/Filter"
import FormSelectFilter from "pages/conversations/molecules/FormSelectFilter"
import Button from "components/Button/Button"
import { useAppSelector } from "hooks/useApp"
import { ChangeRoleModal } from "./modal/ChangeRoleModal"
import { InviteMemberModal } from "./modal/InviteMemberModal"
import { configureStore } from "stores/configureStore"
import { closeModal, openModal } from "reducers/modal"
import { toast } from "react-toastify"
import LabelNotificationPage from "components/Notification/LabelNotificationPage"
import { pushTo } from "helpers/history"
import { hasPermission } from "helpers/permissions"
import { PERMISSIONS } from "constants/permissions"
import { PATH } from "constants/path"
import { ShareKeyProjectModal } from "pages/project-share-organization/modal/ShareKeyProjectModal"
import {
  SelectedUser,
  ShareKeyProject,
} from "pages/project-share-organization/ShareKeyOrganizationPage"
import { getShareKeyProjectMiddleware } from "pages/project-share-organization/project-share-organization.api"
import { encryptionGCMHelper } from "helpers/encryption-gcm"
import { MESSENGER_NOTIFICATION } from "constants/messenger"
import { KeyEncryptionType } from "types"
import { getPrivateKeyByUserId } from "helpers/utils"
import ButtonHasIcon from "components/Button/ButtonHasIcon"
import { getUserMiddleware } from "pages/auth/services/api"
import { addRoleGroupOrganizationMiddleware } from "pages/projects/services/api"
import TabItem from "pages/projects/molecules/TabItem"
import { GroupsPage } from "pages/groups/GroupsPage"
import { FilterHeader } from "components/Filter/FilterHeader"
import { FilterCollapse } from "components/Filter/FilterCollapse"
import { getUserVaultKey } from "helpers/local_storage"

const UserPage = () => {
  const userInfo = useAppSelector((state) => state.userInfo)
  const { isMobile } = useWindowSize()

  const [users, setUsers] = useState<UserResponse[]>([])
  const [queryState, setQueryState] = useState<UserQueryState>({
    page: 1,
    pageSize: 25,
    search: "",
    status: "",
    roles: "",
    sort: "",
    order: "",
  })
  const [roles, setRoles] = useState<
    { id: string; name: string; description: string }[]
  >([])
  const totalPage = useNumber(0)
  const search = useString()
  const [showTippy, setShowTippy] = useState([] as string[])
  const showFilter = useBoolean(false)
  const [filterStatus, setFilterStatus] = useState<{ [key: number]: number }>({
    "-1": -1,
  })
  const [filterRole, setFilterRole] = useState<{ [key: string]: string }>({
    "-1": "-1",
  })

  const [showChangeRoleModal, setShowChangeRoleModal] = useState({
    open: false,
    roleId: "",
    userId: "",
  })
  const [shareKeyProject, setShareKeyProject] = useState<{
    projectKeys: ShareKeyProject[]
    selectedUsers: SelectedUser[]
  }>({
    projectKeys: [],
    selectedUsers: [],
  })
  const isOpenShareKeyProjectModal = useBoolean(false)
  const openModalInviteMember = useBoolean(false)
  const userLimit = useNumber(0)
  const organizationAction = userInfo.organizations?.find(
    (el) => el.id === userInfo.last_organization_id
  )
  const { searchParams, setSearchParams } = useSearchParams()
  const [tab, setTab] = useState<string>("Users")
  const [labelLimit, setLabelLimit] = useState<string>("")
  const { TippyLayout } = useTippyLayout()

  useEffect(() => {
    if (!userInfo?.id) {
      return
    }
    if (!userInfo.last_organization_id) {
      pushTo(PATH.notFound)
      return
    }
    if (!hasPermission(userInfo, PERMISSIONS.USER_VIEW)) {
      pushTo(PATH.notFound)
      return
    }
  }, [userInfo?.id])

  useEffect(() => {
    getAllUser(queryState)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    queryState.pageSize,
    queryState.page,
    queryState.search,
    queryState.status,
    queryState.roles,
    queryState.sort,
    queryState.order,
  ])

  useEffect(() => {
    getAllRole()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Sync tab with URL params (for page reload)
  useEffect(() => {
    const tabFromUrl = searchParams?.tab || "Users"
    if (includes(["Users", "Groups"], tabFromUrl)) {
      setTab(tabFromUrl)
    } else {
      setTab("Users")
      setSearchParams({ tab: "Users" })
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchParams?.tab])

  const getAllUser = async (newQueyState: UserQueryState) => {
    const response = await getAllUserMiddleware(newQueyState)
    setUsers(response.data)
    userLimit.setValue(response.userLimit)
    totalPage.setValue(response.total)
    setLabelLimit(response.labelLimit || ``)
  }

  const getAllRole = async () => {
    const response = await getAllRoleMiddleware()
    setRoles(response.data)
  }

  const handleChangePage = (newPage: number, newLimit: number) => {
    setQueryState({
      ...queryState,
      page: newPage,
      pageSize: newLimit,
    })
  }
  const handleClickTippy = (value) => {
    if (showTippy.includes(value)) {
      setShowTippy(showTippy.filter((item) => item !== value))
    } else setShowTippy(showTippy.concat([value]))
  }

  const onPreventBubble = (e) => {
    e.preventDefault()
    e.stopPropagation()
  }

  const debounceSearch = useCallback(
    debounce((newSearch: string, newQueyState: UserQueryState) => {
      setQueryState({
        ...newQueyState,
        search: newSearch.trim(),
        page: 1,
      })
    }, 500),
    []
  )

  const onCheckActiveFilter = () => {
    return (
      (Object.keys(filterStatus).length > 0 && !filterStatus["-1"]) ||
      (Object.keys(filterRole).length > 0 && !filterRole["-1"])
    )
  }

  const handleSort = (
    dataIndex: string,
    sortDirection: "asc" | "desc" | null
  ) => {
    setQueryState({
      ...queryState,
      sort: sortDirection ? dataIndex : "",
      order: sortDirection || "",
      page: 1, // Reset to first page when sorting
    })
  }

  const handleChangeStatus = (id: string, isBlocked: boolean) => {
    const titleButton = isBlocked ? "Unblock" : "Block"

    const handleSubmit = async () => {
      try {
        await updateUserBlockMiddleware(id)
        getAllUser(queryState)
        toast(
          <LabelNotificationPage
            messenger={`${titleButton}ed user successfully!`}
            type="success"
          />
        )
        closeModal()
      } catch (error: any) {
        closeModal()
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ?? `${titleButton}ed user error!`
            }
            type="error"
          />
        )
      }
    }

    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: isBlocked
              ? `Are you sure you want to unblock this user? They will regain access to the workspace.`
              : `Are you sure you want to block this user? They will lose access to the workspace until unblocked.`,
            label: `Warning`,
            content: `This action cannot be undone.`,
            styleTitle: { textAlign: "center" },
            titleButton: titleButton,
            colorYellowButton: isBlocked,
            onSubmit: handleSubmit,
          },
        },
      })
    )
  }

  const openDeleteUser = (columnData: UserResponse) => {
    const handleSubmit = async () => {
      try {
        await removeUserInWorkspaceMiddleware(columnData.id)
        getAllUser(queryState)
        toast(
          <LabelNotificationPage
            messenger={`Removed user from the workspace successfully!`}
            type="success"
          />
        )
        closeModal()
      } catch (error: any) {
        closeModal()
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ??
              `Removed user from the workspace error!`
            }
            type="error"
          />
        )
      }
    }
    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: `The user will be removed from the workspace and will lose access to it permanently.`,
            label: `Warning`,
            content: `This action cannot be undone.`,
            styleTitle: { textAlign: "center" },
            titleButton: "Remove",
            colorYellowButton: false,
            onSubmit: handleSubmit,
          },
        },
      })
    )
  }
  const openChangeRoleModal = (columnData: UserResponse) => {
    setShowChangeRoleModal({
      open: true,
      roleId: columnData.roleId,
      userId: columnData.id,
    })
  }

  const handleShareKeys = async (userId: string) => {
    const response = await getShareKeyProjectMiddleware(userId)
    setShowChangeRoleModal({
      open: false,
      roleId: "",
      userId: "",
    })
    if (response) {
      if (!response.users?.length) {
        toast(
          <LabelNotificationPage
            messenger={MESSENGER_NOTIFICATION.CHANGE_ROLE_SUCCESS}
            type="success"
          />
        )
        getAllUser(queryState)
        return
      }
      const decryptedKeys: ShareKeyProject[] = []
      if (response?.projectKeys && response?.projectKeys.length) {
        const userVaultKey = getUserVaultKey()
        const privateKeyPem: any = await getPrivateKeyByUserId()
        for (const el of response.projectKeys) {
          let decrypted = el.encryptedKey
          if (el.type === KeyEncryptionType.ProjectShareKey) {
            decrypted = privateKeyPem.decrypt(el.encryptedKey, "RSA-OAEP")
          } else {
            decrypted = await encryptionGCMHelper.decryptContext(
              userVaultKey,
              el.encryptedKey
            )
          }
          if (decrypted && decrypted !== el.encryptedKey) {
            decryptedKeys.push({
              projectKey: decrypted,
              projectId: el.id,
            })
          }
        }
      }
      setShareKeyProject({
        projectKeys: decryptedKeys,
        selectedUsers: response.users,
      })
      isOpenShareKeyProjectModal.setValue(true)
      return
    }
    toast(
      <LabelNotificationPage
        messenger="Failed to get share key project!"
        type="error"
      />
    )
  }
  const handleShareKeyDone = () => {
    isOpenShareKeyProjectModal.setValue(false)
    getAllUser(queryState)
  }

  const handleTransferOwner = (userId: string) => {
    const OWNER_ROLE_ID = "6f076bde-6425-5e1f-9677-3c9728484599"
    const handleSubmit = async () => {
      try {
        await updateUserRoleMiddleware(userId, OWNER_ROLE_ID, true)
        await addRoleGroupOrganizationMiddleware({
          is_share_all_project: true,
          user_ids: JSON.stringify([userId]),
        })
        closeModal()
        handleShareKeys(userId)
        getUserMiddleware()
        toast(
          <LabelNotificationPage
            messenger="Transfer workspace ownership successfully!"
            type="success"
          />
        )
      } catch (error: any) {
        closeModal()
        toast(
          <LabelNotificationPage
            messenger={
              error.response?.data?.message ??
              `Transfer workspace ownership failed!`
            }
            type="error"
          />
        )
      }
    }

    configureStore.dispatch(
      openModal({
        type: "Delete",
        props: {
          deleteModal: {
            title: (
              <div className="flex flex-col gap-[4px]">
                <p
                  className="text-[14px] leading-[22px] font-semibold text-[#222222]"
                  style={{
                    textAlign: "left",
                  }}
                >
                  You are about to transfer the Workspace Owner role to another
                  member.
                </p>
                <p
                  className="text-[14px] leading-[22px] text-[#222222]"
                  style={{
                    textAlign: "left",
                  }}
                >
                  Once the transfer is completed:
                </p>
                <ul className="pl-[16px]">
                  <li
                    style={{
                      textAlign: "left",
                    }}
                  >
                    You will lose all top-level administrative privileges for
                    this workspace.
                  </li>
                  <li
                    style={{
                      textAlign: "left",
                    }}
                  >
                    The new owner will have full control over settings, members,
                    data, and resources.
                  </li>
                  <li
                    style={{
                      textAlign: "left",
                    }}
                  >
                    This action is permanent and cannot be undone.
                  </li>
                </ul>
              </div>
            ),
            label: "Transfer Workspace Ownership",
            content: "Are you sure you want to proceed?",
            styleTitle: { textAlign: "center" },
            titleButton: "Confirm Transfer",
            colorYellowButton: true,
            onSubmit: handleSubmit,
            contentWrapperStyles: {
              paddingTop: 0,
              paddingBottom: 49,
            },
          },
        },
      })
    )
  }

  const onChangeRoleFilter = (chosenValue: any) => {
    const newChosenValue = cloneDeep(chosenValue)
    setFilterRole((prev) => {
      if (
        Object.keys(newChosenValue).length === 0 ||
        (prev["-1"] !== "-1" && newChosenValue["-1"] === "-1")
      ) {
        return { "-1": "-1" }
      }
      if (
        newChosenValue["-1"] === "-1" &&
        Object.keys(newChosenValue).length > 1
      ) {
        delete newChosenValue["-1"]
      }

      return newChosenValue
    })
  }

  const onClearRoleFilter = () => {
    const statusFilter =
      queryState.status && queryState.status !== ""
        ? JSON.parse(queryState.status).reduce(
            (acc, status) => ({ ...acc, [status]: status }),
            {}
          )
        : { "-1": -1 }

    const roleFilter =
      queryState.roles && queryState.roles !== ""
        ? JSON.parse(queryState.roles).reduce(
            (acc, role) => ({ ...acc, [role]: role }),
            {}
          )
        : { "-1": "-1" }

    setFilterStatus(statusFilter)
    setFilterRole(roleFilter)
    showFilter.setValue(false)
  }

  const onApplyFilter = () => {
    showFilter.setValue(false)
    setQueryState({
      ...queryState,
      page: 1,
      status: (() => {
        const filteredStatus = Object.keys(filterStatus)
          .filter((key) => key !== "-1")
          .map((key) => filterStatus[key])
        return filteredStatus.length === 0 ? "" : JSON.stringify(filteredStatus)
      })(),
      roles: (() => {
        const filteredRoles = Object.keys(filterRole)
          .filter((key) => key !== "-1")
          .map((key) => filterRole[key])
        return filteredRoles.length === 0 ? "" : JSON.stringify(filteredRoles)
      })(),
    })
  }

  const renderFilter = () => {
    const rolerFilters = [
      {
        key: "All",
        value: "-1",
      },
      ...roles.map((role) => ({
        key: role.name,
        value: role.id,
      })),
    ]

    if (isMobile) {
      return (
        <TippyLayout
          visible={showFilter.value}
          headerContent={
            <FilterHeader
              setVisible={showFilter.setValue}
              onCancel={onClearRoleFilter}
              onApply={onApplyFilter}
            />
          }
          mainContent={
            <div>
              <ClearAll
                onClick={() => {
                  setFilterStatus({ "-1": -1 })
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
                containerClass="border-b border-[#E4E4E4]"
              />

              <div className="flex flex-col">
                <FilterCollapse
                  label="Role"
                  multiActive
                  data={rolerFilters.map((el) => ({
                    value: el.value,
                    label: el.key,
                  }))}
                  chosenValue={filterRole}
                  onChange={onChangeRoleFilter}
                />
              </div>
            </div>
          }
        >
          <FilterIcon
            onClick={() => {
              showFilter.setValue(!showFilter.value)
            }}
            activeIcon={queryState.status || queryState.roles ? true : false}
          />
        </TippyLayout>
      )
    }
    return (
      <TippyCustomzie
        containerClass="component-card-menu"
        placement="bottom-end"
        interactive
        arrow={false}
        animation="scale"
        offset={[280, 20]}
        allowHTML
        visible={showFilter.value}
        content={
          <div
            className="pt-3.5 pb-3"
            style={{
              background: "#222222",
              borderRadius: 4,
              width: 250,
            }}
          >
            <div className="flex items-center justify-between mb-3 px-3 select-none">
              <p className="hidden md:block font-semibold text-16 leading-26 text-white">
                Filter
              </p>
              <ClearAll
                onClick={() => {
                  setFilterStatus({ "-1": -1 })
                  setFilterRole({ "-1": "-1" })
                }}
                active={onCheckActiveFilter()}
              />
            </div>

            <div className="flex">
              <FormSelectFilter
                title="Role"
                type="multi"
                dataSelects={rolerFilters}
                styleKey={{
                  maxWidth: "100%",
                }}
                styleRoot={{
                  width: "100%",
                }}
                objActiveSelect={filterRole}
                setObjActiveSelect={onChangeRoleFilter}
              />
            </div>

            <div className="flex justify-end mt-3 mr-3">
              <Button
                title="Apply"
                onClick={onApplyFilter}
                widthBtn={120}
                heightBtn={42}
              />
            </div>
          </div>
        }
        onClickOutside={onClearRoleFilter}
      >
        <FilterIcon
          onClick={() => {
            showFilter.setValue(!showFilter.value)
          }}
          activeIcon={queryState.status || queryState.roles ? true : false}
        />
      </TippyCustomzie>
    )
  }

  const renderSearch = () => {
    return (
      <div
        className=" bg-grayWhite2 p-2 rounded-full flex items-center"
        onClick={onPreventBubble}
        style={{
          width: isMobile ? 200 : 320,
        }}
      >
        <IconSearchNormal
          style={{
            width: 20,
          }}
          values={search.value}
          onClick={onPreventBubble}
          onChange={(e) => {
            onPreventBubble(e)
          }}
        />
        <InputDefault
          value={search.value}
          placeholder="Search by either name, email or phone"
          style={{
            border: "none",
            boxShadow: "none",
            height: 30,
            fontWeight: 400,
          }}
          classCustom="h-11 border rounded bg-grayWhite2 pl-3 pr-3 text-sm font-light text-[#111111] focus:outline-none"
          onClick={onPreventBubble}
          onChange={(e) => {
            onPreventBubble(e)
            search.setValue(e.target.value)
            debounceSearch(e.target.value, cloneDeep(queryState))
          }}
        />
        {search.value ? (
          <div
            onClick={(e) => {
              onPreventBubble(e)
              search.setValue("")
              setQueryState({
                ...queryState,
                search: "",
              })
            }}
            className="h-5 w-5 flex items-center justify-center custom-icon-clear-search cursor-pointer"
          >
            <IconClearSearch />
          </div>
        ) : null}
      </div>
    )
  }

  const columnsMobile: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "fullname",
      render: (columnData: UserResponse) => {
        let color = "#F7AC1B"
        let newStatus = columnData.customStatus
        if (!columnData.isBlocked) {
          if (columnData.status === "waiting for verify") {
            newStatus = "Waiting for Verification"
          }
          if (columnData.status === "pending") {
            newStatus = "Pending for Approval"
          }
        }
        if (columnData.customStatus === "Active") {
          color = "#56A012"
        }
        if (columnData.customStatus === "Blocked") {
          color = "#EA4545"
        }
        return (
          <div className="flex flex-col gap-[8px]">
            <div className="flex items-center gap-[8px]">
              <Avatar
                name={columnData.fullname ?? ""}
                avatar={columnData.avatar ?? ""}
                width="30px"
                height="30px"
              />
              <div className="flex flex-col">
                <p className="font-semibold text-[13px] leading-[22px] text-[#111111]">
                  {columnData.fullname}
                </p>
                <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
                  {columnData.email}
                </p>
              </div>
            </div>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Phone: <span className="">{columnData.phone || "-"}</span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Status:{" "}
              <span
                className="font-semibold whitespace-nowrap status-active font-semibold"
                style={{ color: color }}
              >
                {newStatus}
              </span>
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#111111]">
              Role:{" "}
              <span
                style={{
                  whiteSpace: "nowrap",
                  fontWeight: columnData.isOwner ? 600 : 400,
                }}
              >
                {columnData.isOwner ? "Workspace Owner" : columnData.roleName}
              </span>
            </p>
          </div>
        )
      },
    },
  ]

  const columns: SimpleTableProps["columns"] = [
    {
      title: "Name",
      dataIndex: "fullname",
      sortable: true,
      render: (columnData: UserResponse) => (
        <div className="flex items-center gap-[8px] pl-[20px]">
          <Avatar
            name={columnData.fullname ?? ""}
            avatar={columnData.avatar ?? ""}
            width="30px"
            height="30px"
          />
          <div className="flex flex-col">
            <p className="font-semibold text-[13px] leading-[22px] text-[#111111]">
              {columnData.fullname}
            </p>
            <p className="font-normal text-[13px] leading-[22px] text-[#7A7A7A]">
              {columnData.email}
            </p>
          </div>
        </div>
      ),
    },

    {
      title: "Phone",
      dataIndex: "phone",
      sortable: true,
      width: "15%",
      render: (columnData: UserResponse) => columnData.phone || "-",
    },
    {
      title: "Status",
      dataIndex: "customStatus",
      width: "15%",
      render: (columnData: UserResponse) => {
        let color = "#F7AC1B"
        let newStatus = columnData.customStatus
        if (!columnData.isBlocked) {
          if (columnData.status === "waiting for verify") {
            newStatus = "Waiting for Verification"
          }
          if (columnData.status === "pending") {
            newStatus = "Pending for Approval"
          }
        }
        if (columnData.customStatus === "Active") {
          color = "#56A012"
        }
        if (columnData.customStatus === "Blocked") {
          color = "#EA4545"
        }
        return (
          <p
            className="status-active font-semibold"
            style={{
              color: color,
              whiteSpace: "nowrap",
            }}
          >
            {newStatus}
          </p>
        )
      },
    },
    {
      title: "Role",
      dataIndex: "role",
      width: "15%",
      render: (columnData: UserResponse) => {
        return (
          <p
            className="text-[13px] leading-[22px] text-[#111111]"
            style={{
              whiteSpace: "nowrap",
              fontWeight: columnData.isOwner ? 600 : 400,
            }}
          >
            {columnData.isOwner ? "Workspace Owner" : columnData.roleName}
          </p>
        )
      },
    },
    {
      title: "",
      dataIndex: "action",
      align: "right",
      width: "30px",
      render: (columnData: UserResponse) => {
        const currentUser = userInfo.id === columnData.id
        if (currentUser || columnData.isOwner) {
          return null
        }

        const isAssigned = Boolean(columnData.roleName === "Member")

        return (
          <TippyCustomzie
            containerClass="component-card-menu"
            placement="bottom-end"
            interactive
            arrow={false}
            animation="scale"
            visible={showTippy.includes(columnData.id)}
            onClickOutside={() =>
              setShowTippy(showTippy.filter((item) => item !== columnData.id))
            }
            content={
              <div
                onClick={onPreventBubble}
                style={{
                  background: "#222222",
                  borderRadius: 4,
                  paddingTop: 8,
                  paddingBottom: 8,
                  width: 140,
                }}
              >
                {isAssigned ? (
                  <ActionItem
                    title={"Manage Access"}
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      pushTo(
                        PATH.assignment.replace(":userId", columnData.id),
                        undefined,
                        {
                          roleName: columnData.roleName,
                        }
                      )
                    }}
                  />
                ) : null}
                {organizationAction?.type === "OWNER" ? (
                  <ActionItem
                    title={"Transfer owner"}
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      handleTransferOwner(columnData.id)
                    }}
                  />
                ) : null}

                {!columnData.isBlocked ? (
                  <ActionItem
                    title="Change role"
                    onClick={() => {
                      handleClickTippy(columnData.id)
                      openChangeRoleModal(columnData)
                    }}
                  />
                ) : null}
                <ActionItem
                  title={columnData.isBlocked ? "Unblock" : "Block"}
                  onClick={() => {
                    handleClickTippy(columnData.id)
                    handleChangeStatus(columnData.id, columnData.isBlocked)
                  }}
                />
                <ActionItem
                  title="Remove"
                  onClick={() => {
                    handleClickTippy(columnData.id)
                    openDeleteUser(columnData)
                  }}
                />
              </div>
            }
            allowHTML
          >
            <div
              className="flex items-center justify-end cursor-pointer"
              onClick={(e) => {
                e.stopPropagation()
                handleClickTippy(columnData.id)
              }}
            >
              <IconActionThreeDot />
            </div>
          </TippyCustomzie>
        )
      },
    },
  ]

  const renderUserContent = () => {
    return (
      <div className="flex flex-col h-full overflow-hidden">
        <div className="flex items-center justify-between bg-[#FFFFFF] border-b border-[#E4E4E4] min-h-[63px] px-[24px]">
          <h2 className="text-[16px] font-semibold lh-26 text-black">{`Users ${labelLimit}`}</h2>
          <div className="flex items-center gap-[12px]">
            {!isMobile ? renderSearch() : null}
            {renderFilter()}
            {!isMobile &&
            (userLimit.value === -1 || totalPage.value < userLimit.value) ? (
              <ButtonHasIcon
                title="Invite Member"
                widthButton={130}
                onClick={() => openModalInviteMember.setValue(true)}
              />
            ) : null}
          </div>
        </div>
        <div className="flex flex-col py-6 pl-6 flex-1 overflow-hidden">
          <div className="flex flex-col pr-6 overflow-auto">
            <div className="text-black bg-white border border-border-gray rounded-md custom-table pb-[6px]">
              <SimpleTable
                customClass="storage-usage-table"
                columns={isMobile ? columnsMobile : columns}
                dataSource={users}
                onSort={handleSort}
                isRequiredTableHeader={isMobile ? false : true}
              />
            </div>
            {totalPage.value ? (
              <Pagination
                total={totalPage.value}
                queryState={{
                  page: queryState.page,
                  limit: queryState.pageSize,
                }}
                handleChangePage={handleChangePage}
              />
            ) : null}
          </div>
        </div>
        {showChangeRoleModal.open ? (
          <ChangeRoleModal
            isOpen={showChangeRoleModal.open}
            roles={roles}
            roleId={showChangeRoleModal.roleId}
            userId={showChangeRoleModal.userId}
            onCloseModal={() => {
              setShowChangeRoleModal({ open: false, roleId: "", userId: "" })
            }}
            onSuccess={() => {
              getAllUser(queryState)
            }}
            handleShareKeys={handleShareKeys}
          />
        ) : null}
        {openModalInviteMember.value ? (
          <InviteMemberModal
            isOpen={openModalInviteMember.value}
            roles={roles}
            onCloseModal={() => openModalInviteMember.setValue(false)}
            onSuccess={() => {
              if (queryState.page === 1) {
                getAllUser(queryState)
              } else {
                setQueryState({
                  ...queryState,
                  page: 1,
                })
              }
            }}
          />
        ) : null}
        {isOpenShareKeyProjectModal.value && (
          <ShareKeyProjectModal
            onCloseModal={() => isOpenShareKeyProjectModal.setValue(false)}
            projectKeys={shareKeyProject.projectKeys}
            selectedUsers={shareKeyProject.selectedUsers}
            isShareAllKeys={false}
            onDone={handleShareKeyDone}
            isAutoProceed={true}
            isOpen={isOpenShareKeyProjectModal.value}
          />
        )}
      </div>
    )
  }

  const renderHeader = () => {
    return (
      <div className="flex items-center gap-[24px] h-full px-[24px]">
        {["Users", "Groups"].map((item, index) => {
          const isActive = tab === item
          return (
            <div className="relative h-full flex items-center" key={index}>
              <TabItem
                title={item}
                isActive={isActive}
                onClick={() => {
                  setTab(item)
                  setSearchParams({ tab: item })
                  if (item === "Users") {
                    getAllUser(queryState)
                  }
                }}
              />
              {isActive ? (
                <div
                  className="absolute bottom-[-1px] left-0 right-[6px] h-[4px] bg-[#FDBE44]"
                  style={{
                    borderTopLeftRadius: 10,
                    borderTopRightRadius: 10,
                  }}
                ></div>
              ) : null}
            </div>
          )
        })}
      </div>
    )
  }

  return (
    <PageLayout childrenHeader={renderHeader()} heightHeader={48}>
      {tab === "Users" ? renderUserContent() : <GroupsPage isAddGroup />}
    </PageLayout>
  )
}

export default UserPage
